import { useCallback, useEffect, useMemo, useState } from "react";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import "../compnay/ManageCompany.css";
import "./marketplaceOrderTimeoutSettings.css";

const PENALTY_TYPES = [
  { value: "Fixed", label: "Fixed Amount" },
  { value: "Percent", label: "Percentage" },
];

const defaultSettings = {
  settingsID: 1,
  pendingAcceptTimeoutMinutes: 10,
  noRiderAlertMinutes: 15,
  noRiderAlertEmail: "",
  marketplaceGstPercent: 0,
  marketplaceRadiusKm: 5,
  riderOfferTimeoutSeconds: 40,
  riderMaxDispatchCycles: 5,
  riderRetryCooldownSeconds: 30,
  restaurantPenaltyRejectThresholdPercent: 5,
  restaurantPenaltyRejectType: "Fixed",
  restaurantPenaltyRejectAmount: 0,
  restaurantPenaltyRejectPercent: 0,
  restaurantPenaltyRejectIsActive: false,
  restaurantPenaltyCancelThresholdPercent: 5,
  restaurantPenaltyCancelType: "Fixed",
  restaurantPenaltyCancelAmount: 0,
  restaurantPenaltyCancelPercent: 0,
  restaurantPenaltyCancelIsActive: false,
  restaurantPenaltyLatePrepAllowedMinutes: 20,
  restaurantPenaltyLatePrepPerMinuteAmount: 0.5,
  restaurantPenaltyLatePrepIsActive: false,
  isActive: true,
};

const mapApiToSettings = (data) => ({
  settingsID: data.settingsID ?? 1,
  pendingAcceptTimeoutMinutes: data.pendingAcceptTimeoutMinutes ?? 10,
  noRiderAlertMinutes: data.noRiderAlertMinutes ?? 15,
  noRiderAlertEmail: data.noRiderAlertEmail || "",
  marketplaceGstPercent: data.marketplaceGstPercent ?? 0,
  marketplaceRadiusKm: data.marketplaceRadiusKm ?? 5,
  riderOfferTimeoutSeconds: data.riderOfferTimeoutSeconds ?? 40,
  riderMaxDispatchCycles: data.riderMaxDispatchCycles ?? 5,
  riderRetryCooldownSeconds: data.riderRetryCooldownSeconds ?? 30,
  restaurantPenaltyRejectThresholdPercent: data.restaurantPenaltyRejectThresholdPercent ?? 5,
  restaurantPenaltyRejectType:
    data.restaurantPenaltyRejectType === "Percent" ? "Percent" : "Fixed",
  restaurantPenaltyRejectAmount: data.restaurantPenaltyRejectAmount ?? 0,
  restaurantPenaltyRejectPercent: data.restaurantPenaltyRejectPercent ?? 0,
  restaurantPenaltyRejectIsActive: data.restaurantPenaltyRejectIsActive ?? false,
  restaurantPenaltyCancelThresholdPercent: data.restaurantPenaltyCancelThresholdPercent ?? 5,
  restaurantPenaltyCancelType:
    data.restaurantPenaltyCancelType === "Percent" ? "Percent" : "Fixed",
  restaurantPenaltyCancelAmount: data.restaurantPenaltyCancelAmount ?? 0,
  restaurantPenaltyCancelPercent: data.restaurantPenaltyCancelPercent ?? 0,
  restaurantPenaltyCancelIsActive: data.restaurantPenaltyCancelIsActive ?? false,
  restaurantPenaltyLatePrepAllowedMinutes: data.restaurantPenaltyLatePrepAllowedMinutes ?? 20,
  restaurantPenaltyLatePrepPerMinuteAmount: data.restaurantPenaltyLatePrepPerMinuteAmount ?? 0.5,
  restaurantPenaltyLatePrepIsActive: data.restaurantPenaltyLatePrepIsActive ?? false,
  isActive: data.isActive ?? true,
});

const MarketplaceRestaurantPenalties = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_SA_MARKETPLACE_ORDER_TIMEOUT_SETTINGS);
      if (response.data.status && response.data.data) {
        setSettings(mapApiToSettings(response.data.data));
      }
    } catch {
      showErrorToast("Failed to load restaurant penalties");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const saveButtonStyle = useMemo(
    () => ({
      background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
      boxShadow: `0 2px 8px ${themeColor}40`,
    }),
    [themeColor]
  );

  const handleSave = async (e) => {
    e.preventDefault();
    const rejectThreshold = parseFloat(settings.restaurantPenaltyRejectThresholdPercent);
    const rejectAmount = parseFloat(settings.restaurantPenaltyRejectAmount);
    const rejectPercent = parseFloat(settings.restaurantPenaltyRejectPercent);
    const cancelThreshold = parseFloat(settings.restaurantPenaltyCancelThresholdPercent);
    const cancelAmount = parseFloat(settings.restaurantPenaltyCancelAmount);
    const cancelPercent = parseFloat(settings.restaurantPenaltyCancelPercent);
    const latePrepAllowed = parseInt(settings.restaurantPenaltyLatePrepAllowedMinutes, 10);
    const latePrepPerMin = parseFloat(settings.restaurantPenaltyLatePrepPerMinuteAmount);

    if (Number.isNaN(rejectThreshold) || rejectThreshold < 0 || rejectThreshold > 100
      || Number.isNaN(cancelThreshold) || cancelThreshold < 0 || cancelThreshold > 100) {
      showErrorToast("Threshold percentages must be between 0 and 100");
      return;
    }
    if (Number.isNaN(rejectAmount) || rejectAmount < 0
      || Number.isNaN(cancelAmount) || cancelAmount < 0
      || Number.isNaN(latePrepPerMin) || latePrepPerMin < 0) {
      showErrorToast("Penalty amounts must be zero or greater");
      return;
    }
    if (Number.isNaN(rejectPercent) || rejectPercent < 0 || rejectPercent > 100
      || Number.isNaN(cancelPercent) || cancelPercent < 0 || cancelPercent > 100) {
      showErrorToast("Penalty charge percentages must be between 0 and 100");
      return;
    }
    if (!Number.isInteger(latePrepAllowed) || latePrepAllowed < 1 || latePrepAllowed > 1440) {
      showErrorToast("Late prep allowed minutes must be between 1 and 1440");
      return;
    }

    try {
      setLoading(true);
      await axiosInstance.put(APIs.PUT_SA_MARKETPLACE_ORDER_TIMEOUT_SETTINGS, {
        settingsID: 1,
        pendingAcceptTimeoutMinutes: parseInt(settings.pendingAcceptTimeoutMinutes, 10) || 10,
        noRiderAlertMinutes: parseInt(settings.noRiderAlertMinutes, 10) || 15,
        noRiderAlertEmail: (settings.noRiderAlertEmail || "").trim(),
        marketplaceGstPercent: parseFloat(settings.marketplaceGstPercent) || 0,
        marketplaceRadiusKm: parseFloat(settings.marketplaceRadiusKm) || 5,
        riderOfferTimeoutSeconds: parseInt(settings.riderOfferTimeoutSeconds, 10) || 40,
        riderMaxDispatchCycles: parseInt(settings.riderMaxDispatchCycles, 10) || 5,
        riderRetryCooldownSeconds: parseInt(settings.riderRetryCooldownSeconds, 10) || 30,
        restaurantPenaltyRejectThresholdPercent: rejectThreshold,
        restaurantPenaltyRejectType: settings.restaurantPenaltyRejectType,
        restaurantPenaltyRejectAmount: rejectAmount,
        restaurantPenaltyRejectPercent: rejectPercent,
        restaurantPenaltyRejectIsActive: settings.restaurantPenaltyRejectIsActive,
        restaurantPenaltyCancelThresholdPercent: cancelThreshold,
        restaurantPenaltyCancelType: settings.restaurantPenaltyCancelType,
        restaurantPenaltyCancelAmount: cancelAmount,
        restaurantPenaltyCancelPercent: cancelPercent,
        restaurantPenaltyCancelIsActive: settings.restaurantPenaltyCancelIsActive,
        restaurantPenaltyLatePrepAllowedMinutes: latePrepAllowed,
        restaurantPenaltyLatePrepPerMinuteAmount: latePrepPerMin,
        restaurantPenaltyLatePrepIsActive: settings.restaurantPenaltyLatePrepIsActive,
        isActive: settings.isActive,
      });
      showSuccessToast("Restaurant penalties saved");
      fetchSettings();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save restaurant penalties");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`mot-settings-page ${isDarkMode ? "is-dark" : "is-light"}`}>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Restaurant Penalties</li>
          </ol>
          <h1 className="page-header mb-0">Restaurant Penalties</h1>
          <p className="mot-page-desc mb-0 mt-2">
            Set allowed reject and cancel rates. Excess orders are charged at settlement using the
            fixed amount or percentage below. Late prep still charges live when an order becomes Ready.
          </p>
        </div>
      </div>

      <div className="card border-0">
        <div className="tab-content p-3">
          <div
            className="mot-info-banner"
            style={{
              background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
              boxShadow: `0 4px 20px ${themeColor}40`,
            }}
          >
            <div className="mot-info-icon">
              <i className="fa fa-gavel" aria-hidden="true" />
            </div>
            <div>
              <div className="mot-info-title">Settlement threshold rules</div>
              <p className="mot-info-text">
                Reject and cancel rates are measured over each weekly settlement period. Only orders
                above the allowed threshold are charged. Late prep is unchanged.
              </p>
              <div className="mot-summary-pills mt-2">
                <span className="mot-pill">
                  Reject: {settings.restaurantPenaltyRejectIsActive
                    ? `> ${settings.restaurantPenaltyRejectThresholdPercent}%`
                    : "Off"}
                </span>
                <span className="mot-pill">
                  Cancel: {settings.restaurantPenaltyCancelIsActive
                    ? `> ${settings.restaurantPenaltyCancelThresholdPercent}%`
                    : "Off"}
                </span>
                <span className="mot-pill">
                  Late prep: {settings.restaurantPenaltyLatePrepIsActive
                    ? `Rs ${settings.restaurantPenaltyLatePrepPerMinuteAmount}/min`
                    : "Off"}
                </span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSave}>
            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-ban" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">Order rejection penalty</h2>
              </div>
              <div className="mot-section-body">
                <div className="mot-rule-card mot-rule-card-last">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <FormInput
                        label="Rejection threshold (%)"
                        type="number"
                        value={settings.restaurantPenaltyRejectThresholdPercent}
                        onchange={(e) =>
                          setSettings({
                            ...settings,
                            restaurantPenaltyRejectThresholdPercent: e.target.value,
                          })
                        }
                      />
                      <div className="mot-help">
                        Allowed share of marketplace orders the restaurant may reject in a settlement week.
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Penalty type</label>
                      <select
                        className="form-select mot-select"
                        value={settings.restaurantPenaltyRejectType}
                        onChange={(e) =>
                          setSettings({ ...settings, restaurantPenaltyRejectType: e.target.value })
                        }
                      >
                        {PENALTY_TYPES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {settings.restaurantPenaltyRejectType === "Percent" ? (
                      <div className="col-md-6">
                        <FormInput
                          label="Penalty percentage (% of order)"
                          type="number"
                          value={settings.restaurantPenaltyRejectPercent}
                          onchange={(e) =>
                            setSettings({
                              ...settings,
                              restaurantPenaltyRejectPercent: e.target.value,
                            })
                          }
                        />
                      </div>
                    ) : (
                      <div className="col-md-6">
                        <FormInput
                          label="Penalty amount (Rs)"
                          type="number"
                          value={settings.restaurantPenaltyRejectAmount}
                          onchange={(e) =>
                            setSettings({
                              ...settings,
                              restaurantPenaltyRejectAmount: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                    <div className="col-md-6">
                      <label className="form-label">Apply penalty when</label>
                      <div className="mot-readonly-field">
                        Rejected orders &gt; {settings.restaurantPenaltyRejectThresholdPercent || 0}%
                      </div>
                      <div className="mot-help">
                        Excess rejects (oldest first) are charged at settlement.
                      </div>
                    </div>
                  </div>
                  <div className="mot-toggle-row mt-3">
                    <div className="mot-toggle-copy">
                      <div className="mot-toggle-label">Penalty status</div>
                      <div className="mot-help mb-0">
                        Off = reject rates are still tracked but no settlement charge is posted.
                      </div>
                    </div>
                    <ToggleSwitch
                      isChecked={settings.restaurantPenaltyRejectIsActive}
                      onToggle={(e) =>
                        setSettings({
                          ...settings,
                          restaurantPenaltyRejectIsActive: e.target.checked,
                        })
                      }
                      name="rejectPenaltyIsActive"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-times-circle" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">Order cancellation penalty</h2>
              </div>
              <div className="mot-section-body">
                <div className="mot-rule-card mot-rule-card-last">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <FormInput
                        label="Cancellation threshold (%)"
                        type="number"
                        value={settings.restaurantPenaltyCancelThresholdPercent}
                        onchange={(e) =>
                          setSettings({
                            ...settings,
                            restaurantPenaltyCancelThresholdPercent: e.target.value,
                          })
                        }
                      />
                      <div className="mot-help">
                        Allowed share of marketplace orders the restaurant may cancel in a settlement week.
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Penalty type</label>
                      <select
                        className="form-select mot-select"
                        value={settings.restaurantPenaltyCancelType}
                        onChange={(e) =>
                          setSettings({ ...settings, restaurantPenaltyCancelType: e.target.value })
                        }
                      >
                        {PENALTY_TYPES.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {settings.restaurantPenaltyCancelType === "Percent" ? (
                      <div className="col-md-6">
                        <FormInput
                          label="Penalty percentage (% of order)"
                          type="number"
                          value={settings.restaurantPenaltyCancelPercent}
                          onchange={(e) =>
                            setSettings({
                              ...settings,
                              restaurantPenaltyCancelPercent: e.target.value,
                            })
                          }
                        />
                      </div>
                    ) : (
                      <div className="col-md-6">
                        <FormInput
                          label="Penalty amount (Rs)"
                          type="number"
                          value={settings.restaurantPenaltyCancelAmount}
                          onchange={(e) =>
                            setSettings({
                              ...settings,
                              restaurantPenaltyCancelAmount: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                    <div className="col-md-6">
                      <label className="form-label">Apply penalty when</label>
                      <div className="mot-readonly-field">
                        Cancelled orders &gt; {settings.restaurantPenaltyCancelThresholdPercent || 0}%
                      </div>
                      <div className="mot-help">
                        Restaurant cancels only. Excess cancels (oldest first) are charged at settlement.
                      </div>
                    </div>
                  </div>
                  <div className="mot-toggle-row mt-3">
                    <div className="mot-toggle-copy">
                      <div className="mot-toggle-label">Penalty status</div>
                      <div className="mot-help mb-0">
                        Off = cancel rates are still tracked but no settlement charge is posted.
                      </div>
                    </div>
                    <ToggleSwitch
                      isChecked={settings.restaurantPenaltyCancelIsActive}
                      onToggle={(e) =>
                        setSettings({
                          ...settings,
                          restaurantPenaltyCancelIsActive: e.target.checked,
                        })
                      }
                      name="cancelPenaltyIsActive"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-section">
              <div className="mot-section-head">
                <span className="mot-section-icon">
                  <i className="fa fa-clock-o" aria-hidden="true" />
                </span>
                <h2 className="mot-section-title">Late preparation</h2>
              </div>
              <div className="mot-section-body">
                <div className="mot-rule-card mot-rule-card-last">
                  <div className="mot-rule-card-desc mb-3">
                    After accept → Ready, each minute past the allowed prep time is charged at the rate
                    below. Applied live when the order becomes Ready (unchanged).
                  </div>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <FormInput
                        label="Allowed prep time (minutes)"
                        type="number"
                        value={settings.restaurantPenaltyLatePrepAllowedMinutes}
                        onchange={(e) =>
                          setSettings({
                            ...settings,
                            restaurantPenaltyLatePrepAllowedMinutes: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="col-md-6">
                      <FormInput
                        label="Charge per late minute (Rs)"
                        type="number"
                        value={settings.restaurantPenaltyLatePrepPerMinuteAmount}
                        onchange={(e) =>
                          setSettings({
                            ...settings,
                            restaurantPenaltyLatePrepPerMinuteAmount: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="mot-toggle-row mt-3">
                    <div className="mot-toggle-copy">
                      <div className="mot-toggle-label">Enable late prep penalty</div>
                      <div className="mot-help mb-0">
                        Off = reject/cancel settlement rules still apply; late-prep charge does not.
                      </div>
                    </div>
                    <ToggleSwitch
                      isChecked={settings.restaurantPenaltyLatePrepIsActive}
                      onToggle={(e) =>
                        setSettings({
                          ...settings,
                          restaurantPenaltyLatePrepIsActive: e.target.checked,
                        })
                      }
                      name="latePrepPenaltyIsActive"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mot-save-row">
              <div className="mot-save-note">
                Reject/cancel charges apply globally at restaurant settlement. Late prep posts immediately.
              </div>
              <button type="submit" className="mot-save-btn" style={saveButtonStyle} disabled={loading}>
                <i className="fa fa-save me-2" aria-hidden="true" />
                Save penalties
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MarketplaceRestaurantPenalties;
